/**
 * Chrome / Edge "Local Network Access" (LNA) for the Capture Helper.
 *
 * Velora (https) calls Velora Capture Helper on http://127.0.0.1:17470. Since
 * Chrome 142 (and Edge on the same Chromium) a page may reach the loopback
 * address only after the user allows it: Chrome shows "<site> wants to look
 * for and connect to any device on your local network" (Allow / Block). If the
 * prompt is blocked, or dismissed often enough that Chrome stops asking
 * (embargo), every helper call fails with the same TypeError as "nothing is
 * listening": the helper looks uninstalled, the browser camera takes over,
 * and the camera's handpiece button does nothing. There is no page API to
 * re-ask once the user chose Block; only the site settings can undo it.
 *
 * This module tells those cases apart, so the UI can say what is wrong:
 *
 * - `navigator.permissions.query` with the permission's names over time
 *   (`loopback-network` after the 2026 split, `local-network-access`,
 *   `local-network`, the old `private-network-access`); each is tried in a
 *   try/catch because unknown names throw.
 * - an opaque (`no-cors`) probe: it resolves when anything answers on the
 *   helper port even if CORS refuses our origin, so "the helper runs but
 *   refuses this Velora address" is distinguishable from "nothing runs".
 */
import { HELPER_PORTS, HELPER_STATUS_PATH, type FetchLike } from './helper.js';

export type LocalAccessState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** Newest first: the first name the browser knows is the one it enforces. */
export const LOCAL_ACCESS_PERMISSION_NAMES = [
  'loopback-network',
  'local-network-access',
  'local-network',
  'private-network-access',
] as const;

export interface PermissionStatusLike {
  state: string;
  addEventListener?: (type: 'change', l: () => void) => void;
  removeEventListener?: (type: 'change', l: () => void) => void;
}
export interface PermissionsLike {
  query(desc: { name: string }): Promise<PermissionStatusLike>;
}

export interface LocalAccess {
  state: LocalAccessState;
  /** The permission name the browser answered for (null when none is known). */
  name: string | null;
  status: PermissionStatusLike | null;
}

export function browserPermissions(): PermissionsLike | null {
  try {
    const p = (globalThis.navigator as Navigator | undefined)?.permissions;
    return p && typeof p.query === 'function' ? (p as unknown as PermissionsLike) : null;
  } catch {
    return null;
  }
}

/** The browser's Local Network Access permission for this site. Never throws. */
export async function queryLocalAccess(
  perms: PermissionsLike | null = browserPermissions(),
): Promise<LocalAccess> {
  if (!perms) return { state: 'unsupported', name: null, status: null };
  for (const name of LOCAL_ACCESS_PERMISSION_NAMES) {
    try {
      const status = await perms.query({ name });
      const s = status.state;
      if (s === 'granted' || s === 'denied' || s === 'prompt') return { state: s, name, status };
    } catch {
      /* this browser does not know the name: try the next */
    }
  }
  return { state: 'unsupported', name: null, status: null };
}

/**
 * Why the helper could not be reached:
 * - `blocked`: the browser's Local Network Access permission is Block.
 * - `needs_permission`: not decided yet (the prompt is open, was dismissed,
 *   or Chrome stopped asking after repeated dismissals). The helper may or
 *   may not be running; the browser will not let us look.
 * - `origin_refused`: something answers on the helper port but refuses this
 *   Velora address (an older helper, or another program on the port).
 * - `not_running`: the browser allowed the call and nothing answered.
 */
export type HelperMissingReason = 'blocked' | 'needs_permission' | 'origin_refused' | 'not_running';

export function missingReason(
  permission: LocalAccessState,
  opaqueAnswered: boolean,
): HelperMissingReason {
  if (permission === 'denied') return 'blocked';
  if (opaqueAnswered) return 'origin_refused';
  if (permission === 'prompt') return 'needs_permission';
  return 'not_running';
}

/** True when anything answers on one of the helper ports (opaque, CORS not needed). */
export async function probeOpaque(fetchImpl: FetchLike, timeoutMs = 1500): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const one = async (port: number) => {
      await fetchImpl(`http://127.0.0.1:${port}${HELPER_STATUS_PATH}`, {
        mode: 'no-cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: ctrl.signal,
        targetAddressSpace: 'loopback',
      } as RequestInit);
      return true;
    };
    return await Promise.any(HELPER_PORTS.map(one)).catch(() => false);
  } finally {
    clearTimeout(timer);
    ctrl.abort();
  }
}

export interface MissingDiagnosis {
  reason: HelperMissingReason;
  permission: LocalAccessState;
}

/** Why a failed helper probe failed (permission first, then an opaque probe). */
export async function diagnoseMissing(
  fetchImpl: FetchLike,
  perms: PermissionsLike | null,
): Promise<MissingDiagnosis> {
  const access = await queryLocalAccess(perms);
  // With the permission denied the opaque probe fails too; skip it.
  const answered = access.state === 'denied' ? false : await probeOpaque(fetchImpl);
  return { reason: missingReason(access.state, answered), permission: access.state };
}

/* ------------------------------------------------------------------ */
/* Instructions                                                         */
/* ------------------------------------------------------------------ */

export const LOCAL_ACCESS_TITLES: Record<HelperMissingReason, string> = {
  blocked: 'Your browser is blocking Velora from the capture helper',
  needs_permission: 'Allow Velora to reach the capture helper on this PC',
  origin_refused: 'The capture helper on this PC does not accept this Velora address',
  not_running: 'Velora Capture Helper is not running on this PC',
};

export function localAccessSummary(reason: HelperMissingReason): string {
  switch (reason) {
    case 'blocked':
      return 'The browser setting "Local network access" is set to Block for this site, so the camera button cannot reach Velora.';
    case 'needs_permission':
      return 'The browser has not been allowed to reach programs on this PC yet. If it asked ("look for and connect to any device on your local network"), the answer was not Allow, or the question was closed.';
    case 'origin_refused':
      return 'Something answers on the helper port but refuses this Velora address. Install the current helper below; it accepts this address.';
    case 'not_running':
      return 'Nothing answers on this PC. Start Velora Capture Helper (Start menu), or install it.';
  }
}

export type BrowserFamily = 'chrome' | 'edge' | 'other';

export function browserFamily(ua: string = globalThis.navigator?.userAgent ?? ''): BrowserFamily {
  if (/\bEdg(?:e|A|iOS)?\//.test(ua)) return 'edge';
  if (/\b(?:Chrome|CriOS|Chromium)\//.test(ua)) return 'chrome';
  return 'other';
}

/**
 * The browser's own settings page for this site. Pages cannot open
 * chrome:// or edge:// links, so the UI offers it to copy and paste.
 */
export function siteSettingsUrl(origin: string, family: BrowserFamily): string | null {
  const site = encodeURIComponent(origin);
  if (family === 'edge') return `edge://settings/content/siteDetails?site=${site}`;
  if (family === 'chrome') return `chrome://settings/content/siteDetails?site=${site}`;
  return null;
}

/**
 * Calls the helper from a click handler. When the permission is still
 * `prompt` (not Block), a fresh request made with a user gesture makes
 * Chrome ask again. Returns whether the helper answered.
 */
export async function requestLocalAccess(
  fetchImpl: FetchLike = (u, i) => fetch(u, i),
  timeoutMs = 20_000,
): Promise<boolean> {
  const ctrl = new AbortController();
  // Long: the user may be reading the browser's prompt.
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const one = async (port: number) => {
      const res = await fetchImpl(`http://127.0.0.1:${port}${HELPER_STATUS_PATH}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: ctrl.signal,
        targetAddressSpace: 'loopback',
      } as RequestInit);
      if (!res.ok) throw new Error(String(res.status));
      return true;
    };
    return await Promise.any(HELPER_PORTS.map(one)).catch(() => false);
  } finally {
    clearTimeout(timer);
    ctrl.abort();
  }
}
